import { RegisterLinkAccess } from './annuity-register-link';
import { AnnuityPatent } from './annuity-patent';
import { isNullOrUndefined } from '../../utils/is-null-or-undefined';

export enum AnnuityRequiredField {
  Applicant = 'APPLICANT',
  NationalFilingDate = 'NATIONAL_FILING_DATE',
  InternationalApplicationDate = 'INTERNATIONAL_FILING_DATE',
  EffectiveFilingDate = 'EFFECTIVE_FILING_DATE',
  PublicationDate = 'PUBLICATION_DATE',
  GrantDate = 'GRANT_DATE',
  ExaminationRequestDate = 'EXAMINATION_REQUEST_DATE',
  EntitySize = 'ENTITY_SIZE',
  DesignatedStates = 'DESIGNATED_STATES',
  NumberOfClaims = 'NUMBER_OF_CLAIMS',
  NumberOfIndependentClaims = 'NUMBER_OF_INDEPENDENT_CLAIMS',
  IntentToGrantDate = 'INTENT_TO_GRANT_DATE',
  FirstPaymentDate = 'FIRST_PAYMENT_DATE',
  NoticeOfAllowanceDate = 'NOTICE_OF_ALLOWANCE',
  ApplicantIsInventor = 'APPLICANT_IS_INVENTOR',
  WillingToLicense = 'WILLING_TO_LICENSE',
  FilingRoute = 'FILING_ROUTE',
  AgentOfRegistration = 'AGENT_OF_REGISTRATION',
  IsPatentExtended = 'IS_PATENT_EXTENDED',
  RegistrationDate = 'REGISTRATION_DATE',
  NumberOfSubDesigns = 'NUMBER_OF_SUB_DESIGNS',
  SpcExpiryDate = 'SPC_EXPIRY_DATE',
  ParentFilingDate = 'PARENT_FILING_DATE',
  GrantStatus = 'GRANT_STATUS',
  GrantStatusUnitary = 'GRANT_STATUS_UNITARY',
  NumberOfClasses = 'NUMBER_OF_CLASSES',
  RequestConvertRenewalSystemFiled = 'REQUEST_CONVERT_RENEWAL_SYSTEM_FILED',
  TypeOfMark = 'TYPE_OF_MARK',
  ApplicantLocatedInThisCountry = 'APPLICANT_LOCATED_IN_THIS_COUNTRY',
  PriorityDate = 'PRIORITY_DATE',
  SingleTrademarkClass = 'SINGLE_TRADEMARK_CLASS',
  ExcessDesignatedGoods = 'EXCESS_DESIGNATED_GOODS',
  PriorityClaimed = 'PRIORITY_CLAIMED',
  FigurativeMark = 'FIGURATIVE_MARK',
  RenewalCalculationDate = 'RENEWAL_CALCULATION_DATE',
  FrenchPolynesiaApplicable = 'FRENCH_POLYNESIA_APPLICABLE',
  NextRenewalDate  = 'NEXT_RENEWAL_DATE',
  DeclarationOfIncontestability = 'DECLARATION_OF_INCONTESTABILITY',
  LastRenewalDeadline = 'LAST_RENEWAL_DEADLINE',
  TrademarkClasses = 'TRADEMARK_CLASSES',
  IsClassified = 'IS_CLASSIFIED',
  ColorType = 'COLOR_TYPE',
  ItemizedClasses = 'ITEMIZED_CLASSES',
  OfficeOfOrigin = 'OFFICE_OF_ORIGIN',
  DesignType = 'DESIGN_TYPE',
}

export enum AnnuitySpecificRequiredField {
  ExtensionAvailable = 'EXTENSION_AVAILABLE',
  StatesPartialRenewalAvailable = 'STATES_PARTIAL_RENEWAL_AVAILABLE',
  DesignsPartialRenewalAvailable = 'DESIGNS_PARTIAL_RENEWAL_AVAILABLE',
  TrademarksPartialRenewalAvailable = 'TRADEMARKS_PARTIAL_RENEWAL_AVAILABLE',
  DeclarationOfIncontestability = 'DECLARATION_OF_INCONTESTABILITY'
}

export enum AnnuityRequiredFieldPartialType {
  StatesPartialRenewalAvailable = 'STATES_PARTIAL_RENEWAL_AVAILABLE',
  DesignsPartialRenewalAvailable = 'DESIGNS_PARTIAL_RENEWAL_AVAILABLE',
  TrademarksPartialRenewalAvailable = 'TRADEMARKS_PARTIAL_RENEWAL_AVAILABLE'
}

export interface AnnuityRequiredFieldsConfig {
  countryCode?: string;
  annuityPatentRequiredFields?: AnnuityPatentRequiredFields[];
  annuitySpecificRequiredFields?: AnnuitySpecificRequiredFields[];
  spreadsheetConfigured?: boolean;
  vendorConfigured?: boolean;
  partialRenewalType?: AnnuityRequiredFieldPartialType;
  registerLinkAccess?: RegisterLinkAccess;
}

export interface AnnuitySpecificRequiredFields {
  extensionDeadlineShift: number;
  specificRequiredFields: AnnuitySpecificRequiredField[];
}

export interface AnnuityPatentRequiredFields {
  requiredFieldType?: AnnuityRequiredField;
  conditionalRequiredFields?: AnnuityConditionalRequiredField[];
  patentRequiredFields?: { [field: string]: AnnuityRequiredField[] };
  annuitySpecificRequiredFields: AnnuityRequiredField[];
  requiredFieldNote?: string;
}

export interface AnnuityConditionalRequiredField {
  comparisonType: AnnuityConditionalRequiredComparisonType;
  dateValue: string;
  stringValue: string;
  booleanValue: boolean;
  requiredFieldType: AnnuityRequiredField;
  conditionalRequiredFields?: AnnuityConditionalRequiredField[];
}

export enum AnnuityConditionalRequiredComparisonType {
  // Using $lt, etc pattern here to keep consistent with our system filters
  // Later we might change this to use those same enum
  $lt = 'LESS_THAN',
  $gt = 'GREATER_THAN',
  $eq = 'EQUALS',
}

export function isAnnuityFieldRequired(
  config: AnnuityRequiredFieldsConfig,
  field: AnnuityRequiredField,
): boolean {
  if (!config || !field || !config.annuityPatentRequiredFields) {
    return false;
  }

  return config.annuityPatentRequiredFields.some(rf => rf.requiredFieldType === field);
}

export function isFilingRouteFieldRequired(
  config: AnnuityRequiredFieldsConfig,
  filingRoute: string,
  field: AnnuityRequiredField,
): boolean {
  if (!config || !config.annuityPatentRequiredFields || !filingRoute) {
    return false;
  }

  const filingRouteEntry = config.annuityPatentRequiredFields
    .find(rf => rf.requiredFieldType === AnnuityRequiredField.FilingRoute);

  if (!filingRouteEntry || !filingRouteEntry.patentRequiredFields) {
    return false;
  }

  const fieldsForRoute = filingRouteEntry.patentRequiredFields[filingRoute];
  if (!fieldsForRoute) {
    return false;
  }

  return fieldsForRoute.some(f => f === field);
}

// Walks the conditional rule tree the BE sends for the country and evaluates it against the
// patent's current values. The rules themselves are never duplicated here - only the mapping
// of a required field type to the patent property holding its value (getPatentFieldValue).
export function buildVisibleConditionalFields(
  config: AnnuityRequiredFieldsConfig,
  patent: AnnuityPatent,
): Map<AnnuityRequiredField, boolean> {
  const visibleFields = new Map<AnnuityRequiredField, boolean>();

  if (!config || !config.annuityPatentRequiredFields || !patent) {
    return visibleFields;
  }

  for (const field of config.annuityPatentRequiredFields) {
    if (!field.conditionalRequiredFields) {
      continue;
    }

    // IMPORTANT NOTES:
    // First level loop has a different model.
    // We start recursive call on the conditionals
    for (const conditionalLevel1 of field.conditionalRequiredFields) {
      parseConditionalRule(patent, field.requiredFieldType, conditionalLevel1, visibleFields);
    }
  }

  return visibleFields;
}

// Single answer to "should this field be shown for this patent": required outright,
// required by the patent's filing route, or made visible by a conditional rule.
export function isAnnuityFieldVisible(
  config: AnnuityRequiredFieldsConfig,
  patent: AnnuityPatent,
  field: AnnuityRequiredField,
): boolean {
  if (!config || !patent || !field) {
    return false;
  }

  if (isAnnuityFieldRequired(config, field)) {
    return true;
  }

  if (isFilingRouteFieldRequired(config, patent.filingRoute, field)) {
    return true;
  }

  return buildVisibleConditionalFields(config, patent).get(field) === true;
}

function parseConditionalRule(
  patent: AnnuityPatent,
  field: AnnuityRequiredField,
  conditional: AnnuityConditionalRequiredField,
  visibleFields: Map<AnnuityRequiredField, boolean>,
): void {
  const currentValue: any = getPatentFieldValue(patent, field); // Any because it could be any type
  const requiredValue: any = getConditionalValue(conditional); // Any because it could be any type
  let fieldIsVisible = false;

  // ----------- ADD OPERATORS HERE -----------
  if (conditional.comparisonType === AnnuityConditionalRequiredComparisonType.$eq) {
    fieldIsVisible = currentValue === requiredValue;
  }

  if (conditional.comparisonType === AnnuityConditionalRequiredComparisonType.$lt) {
    fieldIsVisible = currentValue < requiredValue;
  }
  // ------------- END OPERATORS --------------

  visibleFields.set(conditional.requiredFieldType, fieldIsVisible);

  if (!conditional.conditionalRequiredFields) {
    return;
  }

  for (const conditionalRecursive of conditional.conditionalRequiredFields) {
    parseConditionalRule(patent, conditional.requiredFieldType, conditionalRecursive, visibleFields);
  }
}

function getPatentFieldValue(patent: AnnuityPatent, field: AnnuityRequiredField): any {
  try {
    if (field === AnnuityRequiredField.FilingRoute) {
      return patent.filingRoute;
    }

    if (field === AnnuityRequiredField.TypeOfMark) {
      return patent.typeOfMark.type;
    }

    if (field === AnnuityRequiredField.PriorityDate) {
      return patent.priorityDate;
    }

    if (field === AnnuityRequiredField.PriorityClaimed) {
      return patent.priorityClaimed;
    }

    if (field === AnnuityRequiredField.EffectiveFilingDate) {
      return patent.effectiveFilingDate;
    }

    // NOTE: Reads effectiveFilingDate, carried over from the original mapping.
    if (field === AnnuityRequiredField.RegistrationDate) {
      return patent.effectiveFilingDate;
    }

    if (field === AnnuityRequiredField.TrademarkClasses) {
      return patent.annuityPatentTrademarkClasses;
    }

    if (field === AnnuityRequiredField.IsClassified) {
      return patent.classified;
    }

    // TODO: Add other field data here
    return null;
  } catch (e) {
    return null;
  }
}

function getConditionalValue(field: AnnuityConditionalRequiredField): any {
  if (field.stringValue) {
    return field.stringValue;
  }

  if (field.dateValue) {
    return field.dateValue;
  }

  if (!isNullOrUndefined(field.booleanValue)) {
    return field.booleanValue;
  }

  return null;
}
